import type { Prisma, PrismaClient } from '@prisma/client';
import { Router, type Request } from 'express';
import { normalisePhone } from '@roovers/shared';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { DOC_TYPES, type DocType } from '../../core/sequence.js';
import { authenticate, requirePermission } from '../auth/auth.middleware.js';
import { createPeriodsForYear } from '../accounting/periods.js';
import { queueEmail, renderTemplate, TEMPLATE_VARIABLES } from '../messaging/messaging.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

const nullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export function createSettingsRouter({ db, config }: Deps) {
  const router = Router();
  router.use(
    ['/settings', '/communications', '/audit-logs'],
    authenticate({ db, config }),
    idempotent(db),
  );

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });

  // ───── company profile ─────
  const companySchema = z.object({
    name: z.string().trim().min(2).max(200).optional(),
    tin: nullableText(50),
    vatNumber: nullableText(50),
    vatRegistered: z.boolean().optional(),
    address: nullableText(500),
    phone: nullableText(50),
    email: z
      .string()
      .trim()
      .email()
      .max(200)
      .or(z.literal(''))
      .transform((v) => v || null)
      .nullable()
      .optional(),
    website: nullableText(200),
    brandColor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #CC0000')
      .optional(),
    paymentInstructions: nullableText(2000),
    fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
  });

  const companyView = (c: Prisma.CompanyGetPayload<object>) => ({
    id: c.id,
    name: c.name,
    baseCurrency: c.baseCurrency,
    timezone: c.timezone,
    tin: c.tin,
    vatNumber: c.vatNumber,
    vatRegistered: c.vatRegistered,
    address: c.address,
    phone: c.phone,
    email: c.email,
    website: c.website,
    brandColor: c.brandColor,
    paymentInstructions: c.paymentInstructions,
    fiscalYearStartMonth: c.fiscalYearStartMonth,
  });

  router.get(
    '/settings/company',
    requirePermission('settings.company.view'),
    asyncHandler(async (req, res) => {
      res.json(
        companyView(await db.company.findUniqueOrThrow({ where: { id: req.auth!.companyId } })),
      );
    }),
  );

  router.patch(
    '/settings/company',
    requirePermission('settings.company.edit'),
    asyncHandler(async (req, res) => {
      const body = parse(companySchema, req.body);
      const phone = body.phone ? normalisePhone(body.phone) : body.phone;
      if (body.phone && !phone)
        throw new HttpError(400, 'INVALID_PHONE', 'Enter a valid phone number');
      const before = await db.company.findUniqueOrThrow({ where: { id: req.auth!.companyId } });
      const after = await db.$transaction(async (tx) => {
        const updated = await tx.company.update({
          where: { id: before.id },
          data: { ...body, ...(body.phone !== undefined ? { phone } : {}) },
        });
        await audit(tx, actor(req), {
          action: 'settings.company_updated',
          entity: 'company',
          entityId: before.id,
          before: companyView(before),
          after: companyView(updated),
        });
        return updated;
      });
      res.json(companyView(after));
    }),
  );

  // ───── currencies and exchange rates ─────
  router.get(
    '/settings/currencies',
    requirePermission('settings.tax.view'),
    asyncHandler(async (_req, res) => {
      res.json({ items: await db.currency.findMany({ orderBy: { code: 'asc' } }) });
    }),
  );

  router.get(
    '/settings/exchange-rates',
    requirePermission('settings.tax.view'),
    asyncHandler(async (req, res) => {
      const rows = await db.exchangeRate.findMany({
        where: { companyId: req.auth!.companyId },
        orderBy: [{ effectiveDate: 'desc' }, { currency: 'asc' }],
        take: 200,
      });
      res.json({ items: rows.map((r) => ({ ...r, rate: r.rate.toString() })) });
    }),
  );

  router.post(
    '/settings/exchange-rates',
    requirePermission('settings.tax.edit'),
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          currency: z
            .string()
            .length(3)
            .transform((v) => v.toUpperCase()),
          rate: z
            .string()
            .regex(/^\d{1,11}(\.\d{1,8})?$/, 'Enter a positive number')
            .refine((v) => Number(v) > 0),
          effectiveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        }),
        req.body,
      );
      const company = await db.company.findUniqueOrThrow({ where: { id: req.auth!.companyId } });
      if (body.currency === company.baseCurrency)
        throw new HttpError(400, 'BASE_CURRENCY', 'The base currency always has rate 1');
      if (!(await db.currency.findUnique({ where: { code: body.currency } }))) {
        throw new HttpError(400, 'UNKNOWN_CURRENCY', 'Unknown currency');
      }
      const row = await db.exchangeRate.upsert({
        where: {
          companyId_currency_effectiveDate: {
            companyId: company.id,
            currency: body.currency,
            effectiveDate: new Date(`${body.effectiveDate}T00:00:00Z`),
          },
        },
        create: {
          companyId: company.id,
          currency: body.currency,
          rate: body.rate,
          effectiveDate: new Date(`${body.effectiveDate}T00:00:00Z`),
          createdBy: req.auth!.userId,
        },
        update: { rate: body.rate },
      });
      await audit(db, actor(req), {
        action: 'settings.exchange_rate_set',
        entity: 'exchange_rate',
        entityId: row.id,
        after: { ...body },
      });
      res.status(201).json({ ...row, rate: row.rate.toString() });
    }),
  );

  // ───── tax rates ─────
  const taxSchema = z.object({
    name: z.string().trim().min(2).max(100),
    code: z
      .string()
      .trim()
      .regex(/^[A-Z0-9_]{2,30}$/, 'Use capital letters, digits and underscores'),
    kind: z.enum(['vat', 'wht']),
    rate: z
      .string()
      .regex(/^\d{1,3}(\.\d{1,4})?$/)
      .refine((v) => Number(v) <= 100, 'Rate must be 0-100'),
    isDefault: z.boolean().default(false),
    active: z.boolean().default(true),
  });

  router.get(
    '/settings/tax-rates',
    requirePermission('settings.tax.view'),
    asyncHandler(async (req, res) => {
      const rows = await db.taxRate.findMany({
        where: { companyId: req.auth!.companyId },
        orderBy: [{ kind: 'asc' }, { id: 'asc' }],
      });
      res.json({ items: rows.map((r) => ({ ...r, rate: r.rate.toString() })) });
    }),
  );

  router.post(
    '/settings/tax-rates',
    requirePermission('settings.tax.edit'),
    asyncHandler(async (req, res) => {
      const body = parse(taxSchema, req.body);
      const dup = await db.taxRate.findUnique({
        where: { companyId_code: { companyId: req.auth!.companyId, code: body.code } },
      });
      if (dup) throw new HttpError(409, 'CODE_TAKEN', 'A tax rate with this code exists');
      const row = await db.$transaction(async (tx) => {
        if (body.isDefault)
          await tx.taxRate.updateMany({
            where: { companyId: req.auth!.companyId, kind: body.kind },
            data: { isDefault: false },
          });
        const created = await tx.taxRate.create({
          data: { companyId: req.auth!.companyId, ...body },
        });
        await audit(tx, actor(req), {
          action: 'settings.tax_rate_created',
          entity: 'tax_rate',
          entityId: created.id,
          after: created,
        });
        return created;
      });
      res.status(201).json({ ...row, rate: row.rate.toString() });
    }),
  );

  router.patch(
    '/settings/tax-rates/:id',
    requirePermission('settings.tax.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const body = parse(taxSchema.omit({ code: true, kind: true }).partial(), req.body);
      const before = await db.taxRate.findFirst({ where: { id, companyId: req.auth!.companyId } });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Tax rate not found');
      const row = await db.$transaction(async (tx) => {
        if (body.isDefault)
          await tx.taxRate.updateMany({
            where: { companyId: before.companyId, kind: before.kind },
            data: { isDefault: false },
          });
        const updated = await tx.taxRate.update({ where: { id }, data: body });
        await audit(tx, actor(req), {
          action: 'settings.tax_rate_updated',
          entity: 'tax_rate',
          entityId: id,
          before: { ...before, rate: before.rate.toString() },
          after: { ...updated, rate: updated.rate.toString() },
        });
        return updated;
      });
      res.json({ ...row, rate: row.rate.toString() });
    }),
  );

  // ───── numbering ─────
  router.get(
    '/settings/numbering',
    requirePermission('settings.numbering.view'),
    asyncHandler(async (req, res) => {
      const year = new Date().getUTCFullYear();
      const rows = await db.documentSequence.findMany({
        where: { companyId: req.auth!.companyId, year },
      });
      const items = (Object.keys(DOC_TYPES) as DocType[]).map((docType) => {
        const row = rows.find((r) => r.docType === docType);
        const prefix = row?.prefix ?? DOC_TYPES[docType].prefix;
        const padding = row?.padding ?? 4;
        const next = (row?.lastNumber ?? 0) + 1;
        return {
          docType,
          prefix,
          padding,
          lastNumber: row?.lastNumber ?? 0,
          example: `${prefix}-${year}-${String(next).padStart(padding, '0')}`,
        };
      });
      res.json({ year, items });
    }),
  );

  router.patch(
    '/settings/numbering/:docType',
    requirePermission('settings.numbering.edit'),
    asyncHandler(async (req, res) => {
      const docType = req.params.docType as DocType;
      if (!(docType in DOC_TYPES)) throw new HttpError(404, 'NOT_FOUND', 'Unknown document type');
      const body = parse(
        z.object({
          prefix: z
            .string()
            .trim()
            .regex(/^[A-Z0-9]{1,10}$/, 'Use 1-10 capital letters or digits'),
          padding: z.number().int().min(3).max(8),
        }),
        req.body,
      );
      const year = new Date().getUTCFullYear();
      const companyId = req.auth!.companyId;
      await db.$executeRaw`INSERT IGNORE INTO document_sequences (companyId, docType, year, prefix, padding, lastNumber)
        VALUES (${companyId}, ${docType}, ${year}, ${body.prefix}, ${body.padding}, 0)`;
      const before = await db.documentSequence.findUniqueOrThrow({
        where: { companyId_docType_year: { companyId, docType, year } },
      });
      const after = await db.documentSequence.update({
        where: { companyId_docType_year: { companyId, docType, year } },
        data: { prefix: body.prefix, padding: body.padding },
      });
      await audit(db, actor(req), {
        action: 'settings.numbering_updated',
        entity: 'document_sequence',
        entityId: docType,
        before,
        after,
      });
      res.json(after);
    }),
  );

  // ───── financial years ─────
  router.get(
    '/settings/financial-years',
    requirePermission('settings.company.view'),
    asyncHandler(async (req, res) => {
      res.json({
        items: await db.financialYear.findMany({
          where: { companyId: req.auth!.companyId },
          orderBy: { startDate: 'desc' },
        }),
      });
    }),
  );

  router.post(
    '/settings/financial-years',
    requirePermission('settings.company.edit'),
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          name: z.string().trim().min(3).max(50),
          startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        }),
        req.body,
      );
      if (body.endDate <= body.startDate)
        throw new HttpError(400, 'BAD_RANGE', 'End date must be after the start date');
      const overlap = await db.financialYear.findFirst({
        where: {
          companyId: req.auth!.companyId,
          startDate: { lte: new Date(`${body.endDate}T00:00:00Z`) },
          endDate: { gte: new Date(`${body.startDate}T00:00:00Z`) },
        },
      });
      if (overlap) throw new HttpError(409, 'OVERLAP', `Overlaps ${overlap.name}`);
      const row = await db.$transaction(async (tx) => {
        const created = await tx.financialYear.create({
          data: {
            companyId: req.auth!.companyId,
            name: body.name,
            startDate: new Date(`${body.startDate}T00:00:00Z`),
            endDate: new Date(`${body.endDate}T00:00:00Z`),
          },
        });
        await createPeriodsForYear(tx, req.auth!.companyId, created);
        return created;
      });
      await audit(db, actor(req), {
        action: 'settings.financial_year_created',
        entity: 'financial_year',
        entityId: row.id,
        after: row,
      });
      res.status(201).json(row);
    }),
  );

  // ───── message templates ─────
  router.get(
    '/settings/templates',
    requirePermission('settings.template.view'),
    asyncHandler(async (req, res) => {
      const rows = await db.messageTemplate.findMany({
        where: { companyId: req.auth!.companyId },
        orderBy: [{ docType: 'asc' }, { channel: 'asc' }, { name: 'asc' }],
      });
      res.json({ items: rows, variables: TEMPLATE_VARIABLES });
    }),
  );

  router.put(
    '/settings/templates/:id',
    requirePermission('settings.template.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const body = parse(
        z.object({
          subject: z.string().trim().max(300).nullable().optional(),
          body: z.string().min(1).max(5000),
        }),
        req.body,
      );
      if (body.subject && /[\r\n]/.test(body.subject))
        throw new HttpError(400, 'INVALID_HEADER', 'Subject must not contain line breaks');
      const before = await db.messageTemplate.findFirst({
        where: { id, companyId: req.auth!.companyId },
      });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Template not found');
      const after = await db.messageTemplate.update({
        where: { id },
        data: { subject: body.subject ?? null, body: body.body },
      });
      await audit(db, actor(req), {
        action: 'settings.template_updated',
        entity: 'message_template',
        entityId: id,
        before,
        after,
      });
      res.json(after);
    }),
  );

  // ───── email test + communication log ─────
  router.post(
    '/settings/email/test',
    requirePermission('settings.communication.send'),
    asyncHandler(async (req, res) => {
      const { to } = parse(z.object({ to: z.string().trim().email() }), req.body);
      const company = await db.company.findUniqueOrThrow({ where: { id: req.auth!.companyId } });
      const comms = await queueEmail(db, {
        companyId: company.id,
        to: [to],
        subject: `Test email from ${company.name}`,
        text: renderTemplate(
          'This is a test message from {{company_name}}. If you can read this, email sending works.',
          { company_name: company.name },
        ),
        templateName: 'test',
        sentById: req.auth!.userId,
      });
      await audit(db, actor(req), {
        action: 'settings.email_test_sent',
        entity: 'communication',
        entityId: comms[0]?.id,
        after: { to },
      });
      res.status(202).json({ id: comms[0]?.id, status: 'queued' });
    }),
  );

  router.get(
    '/communications',
    requirePermission('settings.communication.view'),
    asyncHandler(async (req, res) => {
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const q = req.query as Record<string, string | undefined>;
      const where: Prisma.CommunicationWhereInput = {
        companyId: req.auth!.companyId,
        ...(q.status ? { status: q.status } : {}),
        ...(q.channel ? { channel: q.channel } : {}),
        ...(q.docType ? { docType: q.docType } : {}),
        ...(q.docId ? { docId: Number(q.docId) } : {}),
      };
      const [total, items] = await Promise.all([
        db.communication.count({ where }),
        db.communication.findMany({
          where,
          orderBy: { id: 'desc' },
          skip,
          take,
          omit: { bodyText: true },
        }),
      ]);
      res.json({ items, total, page, pageSize });
    }),
  );

  // ───── audit log (read-only) ─────
  router.get(
    '/audit-logs',
    requirePermission('settings.audit.view'),
    asyncHandler(async (req, res) => {
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>, 50);
      const q = req.query as Record<string, string | undefined>;
      const where: Prisma.AuditLogWhereInput = {
        companyId: req.auth!.companyId,
        ...(q.entity ? { entity: q.entity } : {}),
        ...(q.entityId ? { entityId: q.entityId } : {}),
        ...(q.action ? { action: { contains: q.action } } : {}),
        ...(q.userId ? { userId: Number(q.userId) } : {}),
        ...(q.from || q.to
          ? {
              createdAt: {
                ...(q.from ? { gte: new Date(q.from) } : {}),
                ...(q.to ? { lte: new Date(q.to) } : {}),
              },
            }
          : {}),
      };
      const [total, rows] = await Promise.all([
        db.auditLog.count({ where }),
        db.auditLog.findMany({ where, orderBy: { id: 'desc' }, skip, take }),
      ]);
      const userIds = [...new Set(rows.map((r) => r.userId).filter((v): v is number => v != null))];
      const users = await db.user.findMany({
        where: { id: { in: userIds } },
        select: { id: true, name: true },
      });
      const names = new Map(users.map((u) => [u.id, u.name]));
      res.json({
        items: rows.map((r) => ({
          ...r,
          id: r.id.toString(),
          userName: r.userId ? (names.get(r.userId) ?? null) : null,
        })),
        total,
        page,
        pageSize,
      });
    }),
  );

  return router;
}
